import { FC, useCallback, useMemo } from "react";

import { copy } from "shared/assets";
import { Company, Marker } from "shared/assets/icons";
import { FlexContainer, Icon, MutedDarkText, TinyText } from "shared/styles";
import { Spacer } from "shared/layout";
import { colors } from "shared/styles/constants";
import { getCountry } from "shared/utils";
import { ICompany } from "Bands/types";
import { Card, IActionDropdownItem } from "shared/components";
import { ICardTheme } from "shared/types";

const infoCopy = copy.companyInfo;
const propertiesCopy = copy.companyProperties;

interface Props {
  company: ICompany;
  actions?: IActionDropdownItem[];
  cardTheme?: ICardTheme;
}

export const CompanyInfo: FC<Props> = ({ company, actions, cardTheme }) => {
  const renderInfoBlock = useCallback(
    (title: string, content: string | JSX.Element) => (
      <Spacer size="xxs">
        <TinyText as={MutedDarkText}>{title}</TinyText>
        <div>{content}</div>
      </Spacer>
    ),
    []
  );

  const address = useMemo(() => {
    const {
      billingAddress: { addressLine1, addressLine2, zip, city, country },
    } = company;

    const parts = [addressLine1];

    if (addressLine2) {
      parts.push(addressLine2);
    }

    parts.push(`${zip} ${city}`, getCountry(country)?.name || "");

    return parts;
  }, [company]);

  return (
    <Card actions={actions} icon={Company} title={infoCopy.title} theme={cardTheme}>
      <Spacer size="m">
        {renderInfoBlock(propertiesCopy.name.title, company.name)}
        {renderInfoBlock(
          propertiesCopy.billingAddress.title,
          <FlexContainer>
            <Icon as={Marker} color={colors.grey5} />
            <span>{address.join(", ")}</span>
          </FlexContainer>
        )}
        {company?.vat && renderInfoBlock(propertiesCopy.vatNumber.title, company.vat)}
      </Spacer>
    </Card>
  );
};
